/* eslint-disable react-hooks/rules-of-hooks */
import {
    ChangeEvent,
    Fragment,
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LiftOperatorFormSearchField } from './LiftOperatorFormSearchField';
import { DrawerActions } from '@starjack/shared-components';
import { LoadingButton } from '@mui/lab';
import {
    CreateLiftOperator,
    isOfTypeLiftOperator,
    LiftOperator,
} from '@/models/liftoperator';
import { LiftOperatorFormCard } from './LiftOperatorFormCard';
import { cloneDeep, debounce, set } from 'lodash';
import {
    FormDataConfig,
    liftOperatorFormData,
    LiftOperatorFormField,
    LiftOperatorFormGroup,
} from '@/pageContents/liftOperatorsContent/formDataConfig';
import { Money } from '@/models/money';
import { CreateFacility, Facility } from '@/models/facility';
import * as Yup from 'yup';
import {
    usePutLiftOperatorMutation,
    usePostLiftOperatorMutation,
} from '@/api/v2/liftOperator';
import { useNotification } from '@starjack/features/notifications';
import { FormField } from '@/components/FormField/FormField';
import { isValidSeoMetaData, SeoMetaData } from '@/models/seoMetaData';
import { useFormErrorContext } from '@/context/FormErrorContext';
import isEqual from 'lodash/isEqual';
import { useDirtyGuard } from '@starjack/shared-components';
import { IntegrationTokenCard } from './IntegrationTokenCard';
import { useAuthentication } from '@/hooks/useAuthentication';
import jwt_decode from 'jwt-decode';

const INTEGRATION_TOKEN_LABEL = 'integrationstoken';

// useAuthentication maps every user type it does not know (lift operator users
// included) to Permission.ADMIN, so the admin check reads the JWT groups.
const hasAdminGroup = (jwt: string | undefined) => {
    if (!jwt) return false;
    try {
        const { g } = jwt_decode<{ g?: string[] }>(jwt);
        return Array.isArray(g) && g.includes('admin');
    } catch {
        return false;
    }
};

// Mirrors the server-side check (liftoperators EmailAddresses.isStrictlyValid):
// exactly one address, no whitespace, at least two domain labels. Without it
// a 400 support_email_invalid only surfaced as the generic save error.
const STRICT_EMAIL =
    /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

const validationSchema = Yup.object().shape({
    name: Yup.object().shape({
        de: Yup.string().required('Bitte gib einen Namen ein.\n'),
    }),
    address: Yup.object().shape({
        street: Yup.string().required('Bitte gib eine Straße ein.\n'),
        zipcode: Yup.string().required('Bitte gib eine PLZ ein.\n'),
        city: Yup.string().required('Bitte gib einen Ort ein.\n'),
    }),
    billingMode: Yup.string().required('Bitte wähle einen Abrechnungsmodus.\n'),
    integration: Yup.string().required('Bitte wähle eine Integration.\n'),
    supportEmail: Yup.string()
        .trim()
        .nullable()
        .test(
            'strict-email',
            'Bitte eine gültige Support-E-Mail eingeben (genau eine Adresse).\n',
            (value) =>
                !value || (value.length <= 254 && STRICT_EMAIL.test(value))
        ),
    sixCredentials: Yup.object().test(
        'all-or-none',
        'Bitte alle SIX-Felder ausfüllen oder alle frei lassen',
        (value) => {
            const sixFields = [
                'customerId',
                'terminalId',
                'password',
                'username',
            ];

            const filledFields = sixFields.filter((field) => !!value?.[field]);
            return (
                filledFields.length === 0 ||
                filledFields.length === sixFields.length
            );
        }
    ),
});

const findGroupForField = (
    fieldPath: string
): LiftOperatorFormGroup | undefined => {
    return liftOperatorFormData.groups.find(
        (group) =>
            group.name === fieldPath ||
            group.fields.some((f) => f.name === fieldPath) ||
            group.fields.some((f) =>
                f.children?.some((c) => c.name === fieldPath)
            )
    );
};

type LiftOperatorDrawerProps = {
    liftOperator: LiftOperator | CreateLiftOperator;
    onClose: () => void;
};

export const LiftOperatorDrawerContent: FunctionComponent<
    LiftOperatorDrawerProps
> = ({ liftOperator, onClose }) => {
    if (!liftOperator) return null;
    const [localLiftOperator, setLocalLiftOperator] = useState(liftOperator);
    // The drawer stays open after saving; compare against what was saved last.
    const [savedLiftOperator, setSavedLiftOperator] = useState(liftOperator);
    useDirtyGuard(!isEqual(localLiftOperator, savedLiftOperator));
    const [putLiftOperator, { isLoading: putLoading }] =
        usePutLiftOperatorMutation();
    const [postLiftOperator, { isLoading: postLoading }] =
        usePostLiftOperatorMutation();
    const [, setError, clearErrors] = useFormErrorContext();
    const { addNotification } = useNotification();
    const [searchQuery, setSearchQuery] = useState('');
    const { jwt } = useAuthentication();
    const isAdmin = useMemo(() => hasAdminGroup(jwt), [jwt]);
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [scrollToError, setScrollToError] = useState<{
        groupName: string;
        fieldName: string;
    } | null>(null);

    useEffect(() => {
        if (!scrollToError) return;

        // Wait for the accordion expand animation to finish,
        // then use rAF to ensure the DOM has laid out before scrolling
        const timeout = setTimeout(() => {
            requestAnimationFrame(() => {
                const element = document.querySelector(
                    `[name="${scrollToError.fieldName}"]`
                );
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
                setScrollToError(null);
            });
        }, 400);

        return () => clearTimeout(timeout);
    }, [scrollToError]);

    const debouncedSetQuery = useCallback(
        debounce((value: string) => {
            setDebouncedQuery(value);
        }, 200),
        []
    );

    const onSearchChange = (value: string) => {
        setSearchQuery(value);
        debouncedSetQuery(value);
    };

    const filteredGroups = useMemo(() => {
        const query = debouncedQuery.trim().toLowerCase();
        if (!query) return null;

        return liftOperatorFormData.groups
            .map((group) => {
                if (group.label.toLowerCase().includes(query)) return group;

                const matchingFields = group.fields.filter((field) =>
                    field.label.toLowerCase().includes(query)
                );

                if (matchingFields.length === 0) return null;

                return { ...group, fields: matchingFields };
            })
            .filter(Boolean);
    }, [debouncedQuery]);

    const onSaveOrUpdateLiftOperator = (
        lOToUpdate: LiftOperator | CreateLiftOperator
    ) => {
        const isLiftOperator = isOfTypeLiftOperator(lOToUpdate);
        const action = isLiftOperator ? putLiftOperator : postLiftOperator;

        // clean invalid slug meta data objects (when not all fields are filled)
        const clonedLO = cloneDeep(lOToUpdate);
        clonedLO.metaData = clonedLO.metaData.filter(isValidSeoMetaData);

        clonedLO.facilities = clonedLO.facilities.map((facility) => ({
            ...facility,
            metaData: facility.metaData.filter(isValidSeoMetaData),
        }));

        action(clonedLO as LiftOperator)
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Bergbahn erfolgreich gespeichert.',
                    type: 'success',
                });
                setSavedLiftOperator(localLiftOperator);
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern der Bergbahn. Bitte erneut versuchen.',
                    type: 'error',
                });
            });
    };

    const onSubmit = async () => {
        clearErrors();

        // execute onBeforeSubmit handlers
        const cleanedLiftOperator = executeOnBeforeSubmitHandlers(
            liftOperatorFormData,
            localLiftOperator
        );

        try {
            const validatedLiftOperator = (await validationSchema.validate(
                cleanedLiftOperator,
                {
                    abortEarly: false,
                }
            )) as LiftOperator;

            // Quickfix
            const sixFields = [
                'customerId',
                'terminalId',
                'password',
                'username',
            ];
            const { sixCredentials } = validatedLiftOperator;
            const allSixValuesSet = sixFields.every(
                (fieldName) => !!sixCredentials?.[fieldName]
            );

            if (!allSixValuesSet) {
                validatedLiftOperator.sixCredentials = null;
            }

            onSaveOrUpdateLiftOperator(validatedLiftOperator as LiftOperator);
        } catch (e) {
            let scrollTargetField: string | null = null;

            e.inner.forEach((error) => {
                const { path, message } = error;

                // Check if this error path matches a group name (e.g. "sixCredentials")
                // rather than a specific field — if so, also set error on the first field
                // in that group so the scroll target can find a DOM element
                const group = findGroupForField(path);
                if (group?.name === path && group.fields.length > 0) {
                    setError(path, message);
                    setError(group.fields[0].name, message);
                    if (!scrollTargetField) {
                        scrollTargetField = group.fields[0].name;
                    }
                } else {
                    setError(path, message);
                    if (!scrollTargetField) {
                        scrollTargetField = path;
                    }
                }
            });

            if (scrollTargetField) {
                const group = findGroupForField(scrollTargetField);
                if (group?.name) {
                    setScrollToError({
                        groupName: group.name,
                        fieldName: scrollTargetField,
                    });
                }
            }
        }
    };

    const onCheckboxChange = (field: LiftOperatorFormField) => (e) => {
        const { name, checked } = e.target;

        setLocalLiftOperator((prev) => {
            const copy = cloneDeep(prev);

            if (field.onChange) {
                return field.onChange(checked as boolean, copy, field);
            } else {
                set(copy, name, checked);
            }

            return copy;
        });
    };

    const onTextInputChange = (
        nextVal: number | string | Money,
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const { name } = e.target;

        setLocalLiftOperator((prev) => {
            const copy = cloneDeep(prev);

            if (name === 'integration') {
                switch (nextVal as LiftOperator['integration']) {
                    case 'NONE':
                        delete copy.dta;
                        delete copy.bei;
                        delete copy.axess;
                        break;
                    case 'DTA':
                        delete copy.bei;
                        delete copy.axess;
                        break;
                    case 'BEI':
                        delete copy.dta;
                        delete copy.axess;
                        break;
                    case 'AXESS':
                        delete copy.dta;
                        delete copy.bei;
                        break;
                }
            }

            set(copy, name, nextVal);

            return copy;
        });
    };

    const onFacilitiesChange = (value: (Facility | CreateFacility)[]) => {
        setLocalLiftOperator((prev) => {
            const copy = cloneDeep(prev);

            copy.facilities = value;

            return copy;
        });
    };

    const onSeoMetaDataChange = (value: SeoMetaData) => {
        setLocalLiftOperator((prev) => {
            const copy = cloneDeep(prev);

            const seoMetaDataIndex = copy.metaData.findIndex(
                (meta) => meta.language === value.language
            );

            const nextValue = { ...value };
            if (seoMetaDataIndex > -1) {
                copy.metaData[seoMetaDataIndex] = nextValue;
            } else {
                copy.metaData = [...copy.metaData, nextValue];
            }

            return copy;
        });
    };

    return (
        <Box
            sx={{
                width: '800px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                {isOfTypeLiftOperator(liftOperator) ? (
                    <Typography variant='h6'>
                        Bergbahn bearbeiten - {liftOperator.name.de}
                    </Typography>
                ) : (
                    <Typography variant='h6'>
                        Neue Bergbahn erstellen
                    </Typography>
                )}
            </Box>

            {isOfTypeLiftOperator(liftOperator) && (
                <Typography
                    sx={{
                        pl: 4,
                        mt: 3,
                        mb: 2,
                        fontWeight: 'bold',
                    }}
                >
                    ID: {liftOperator.id}
                </Typography>
            )}

            <Box sx={{ px: 4, pt: 4 }}>
                <LiftOperatorFormSearchField
                    value={searchQuery}
                    onChange={onSearchChange}
                />
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={4}
            >
                {(filteredGroups ?? liftOperatorFormData.groups).map(
                    (group, index) => (
                        <LiftOperatorFormCard
                            title={group.label}
                            description={group.description}
                            key={index}
                            groupName={group.name}
                            forceExpanded={!!filteredGroups?.length}
                            forceExpandedForError={
                                scrollToError?.groupName === group.name
                            }
                        >
                            <Stack key={index} spacing={3}>
                                {group.fields.map((field, index) => (
                                    <Fragment key={index}>
                                        <FormField
                                            field={field}
                                            liftOperator={localLiftOperator}
                                            onCheckboxChange={onCheckboxChange(
                                                field
                                            )}
                                            onTextInputChange={
                                                onTextInputChange
                                            }
                                            onFacilitiesChange={
                                                onFacilitiesChange
                                            }
                                            onSeoMetaDataChange={
                                                onSeoMetaDataChange
                                            }
                                        />
                                    </Fragment>
                                ))}
                            </Stack>
                        </LiftOperatorFormCard>
                    )
                )}

                {isOfTypeLiftOperator(liftOperator) &&
                isAdmin &&
                INTEGRATION_TOKEN_LABEL.includes(
                    debouncedQuery.trim().toLowerCase()
                ) ? (
                    <IntegrationTokenCard
                        key={liftOperator.id}
                        liftOperatorId={liftOperator.id}
                    />
                ) : null}
            </Stack>

            <DrawerActions>
                <Stack spacing={2} direction='row'>
                    <Button onClick={onClose} variant='outlined'>
                        Abbrechen
                    </Button>
                    <LoadingButton
                        onClick={onSubmit}
                        variant='contained'
                        loading={postLoading || putLoading}
                    >
                        Speichern
                    </LoadingButton>
                </Stack>
            </DrawerActions>
        </Box>
    );
};

const executeOnBeforeSubmitHandlers = (
    formDataConfig: FormDataConfig,
    liftOperator: LiftOperator | CreateLiftOperator
) => {
    let liftOperatorCopy = cloneDeep(liftOperator);

    formDataConfig.groups.forEach((group) => {
        // execute onBeforeSubmit handlers for each field in the group
        if (group.onBeforeSubmit) {
            liftOperatorCopy = group.onBeforeSubmit(liftOperatorCopy, group);
        }
        group.fields.forEach((field) => {
            if (field.onBeforeSubmit) {
                liftOperatorCopy = field.onBeforeSubmit(
                    liftOperatorCopy,
                    field
                );
            }
        });
    });

    return liftOperatorCopy;
};
